import { omit } from 'lodash-es';
import { createAppSlice } from '.';
import {
  addDefaultArticleToTaskType,
  addMetadataToTaskType,
  addTaskType,
  deleteTaskType as deleteApiTaskType,
  getTaskTypes,
  removeDefaultArticleFromTaskType,
  removeMetadataFromTaskType,
  updateTaskType as updateApiTaskType,
  updateMetadataInTaskType,
} from '../api/api';
import { TaskMetadata } from '../api/queries/workOrders';
import { TASK_TYPE_STATUS, TaskTypeStatus } from '../constants/taskTypeConstants';
import { ItemWithPermissions } from '../utils/permissions';
import { formatApiTaskType, formatApiTaskTypeTemplate, isPhytoTaskType } from '../utils/taskTypes';
import { PartialWithRequired } from '../utils/types';
import { ArticleId } from './articles';
import { ClusterId } from './clusters';
import { alerting, success } from './snacks';

export type TaskTypeId = string;
export type TaskType = ItemWithPermissions & {
  id: TaskTypeId;
  // no cluster_id = template
  cluster_id?: ClusterId;
  label: string;
  color?: string;
  status: TaskTypeStatus;
  group_by?: string;
  parent_task_type_id?: TaskTypeId;
  task_count?: number;
  metadata: TaskMetadata[];
  default_article_ids: ArticleId[];
};

type State = {
  taskTypesByClusterId: Record<ClusterId, TaskType[]>;
  taskTypeTemplates: TaskType[];
  phytoTaskTypes: TaskType[];
};

const initialState: State = {
  taskTypesByClusterId: {},
  taskTypeTemplates: [],
  phytoTaskTypes: [],
};

const taskTypesSlice = createAppSlice({
  name: 'taskTypes',
  initialState,
  reducers: (create) => ({
    fetchAllTaskTypes: create.asyncThunk(
      async (_, { dispatch }) => {
        const taskTypes = (await alerting(getTaskTypes, { dispatch })) as TaskType[];
        return taskTypes.map(formatApiTaskType);
      },
      {
        fulfilled: (state, { payload }) => {
          state.taskTypeTemplates = [];
          state.taskTypesByClusterId = {};
          state.phytoTaskTypes = [];
          for (const taskType of payload) {
            if (isPhytoTaskType(taskType)) {
              state.phytoTaskTypes.push(taskType);
            } else if (taskType.cluster_id) {
              state.taskTypesByClusterId[taskType.cluster_id] ??= [];
              state.taskTypesByClusterId[taskType.cluster_id]!.push(taskType);
            } else {
              state.taskTypeTemplates.push(formatApiTaskTypeTemplate(taskType));
            }
          }
        },
      },
    ),
    createTaskType: create.asyncThunk(
      async (taskType: Partial<TaskType>, { dispatch }) => {
        const newTaskType = (await alerting(
          () => addTaskType({ ...taskType, metadata: (taskType.metadata || []).filter((m) => !m.is_instruction) }),
          { dispatch },
        )) as TaskType;
        success('TaskType.snack_create', { dispatch });
        return formatApiTaskType(newTaskType);
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload.cluster_id) {
            state.taskTypesByClusterId[payload.cluster_id] ??= [];
            state.taskTypesByClusterId[payload.cluster_id]?.push(payload);
          }
        },
      },
    ),
    updateTaskType: create.asyncThunk(
      async (
        taskType: PartialWithRequired<TaskType, 'id'> & {
          previous_metadata?: TaskType['metadata'];
          previous_article_ids?: TaskType['default_article_ids'];
        },
        { dispatch },
      ) => {
        const promises: Promise<unknown>[] = [];
        if (taskType.metadata) {
          const metadataNoInstruction = taskType.metadata.filter((metadata) => !metadata.is_instruction);
          const updatedMetadata = metadataNoInstruction.filter((metadata) => metadata.id);
          for (const metadata of updatedMetadata) {
            promises.push(updateMetadataInTaskType(taskType.id, metadata));
          }

          const addedMetadata = metadataNoInstruction.filter((metadata) => !metadata.id);
          for (const metadata of addedMetadata) {
            promises.push(addMetadataToTaskType(taskType.id, metadata));
          }

          const previousMetadataNoInstruction = (taskType.previous_metadata || []).filter(
            (metadata) => !metadata.is_instruction,
          );
          const removedMetadata = previousMetadataNoInstruction.filter(
            (metadata) => !metadataNoInstruction.find((updatedMetadata) => updatedMetadata.id === metadata.id),
          );
          for (const metadata of removedMetadata) {
            promises.push(removeMetadataFromTaskType(taskType.id, metadata.id));
          }
        }

        if (taskType.default_article_ids) {
          const addedArticles = taskType.default_article_ids.filter(
            (articleId) => !taskType.previous_article_ids?.includes(articleId),
          );
          for (const articleId of addedArticles) {
            promises.push(addDefaultArticleToTaskType(taskType.id, articleId));
          }

          const removedArticles = (taskType.previous_article_ids || []).filter(
            (articleId) => !taskType.default_article_ids?.includes(articleId),
          );
          for (const articleId of removedArticles) {
            promises.push(removeDefaultArticleFromTaskType(taskType.id, articleId));
          }
        }
        await alerting(() => Promise.all(promises), { dispatch });

        const newTaskType = (await alerting(
          () =>
            updateApiTaskType(
              omit(taskType, ['default_article_ids', 'previous_article_ids', 'metadata', 'previous_metadata']),
            ),
          { dispatch },
        )) as TaskType;

        success('TaskType.snack_update', { dispatch });
        return formatApiTaskType(newTaskType);
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload.cluster_id) {
            state.taskTypesByClusterId[payload.cluster_id] ??= [];
            state.taskTypesByClusterId[payload.cluster_id] = [
              ...state.taskTypesByClusterId[payload.cluster_id]!.filter((taskType) => taskType.id !== payload.id),
              payload,
            ];
          }
        },
      },
    ),
    archiveTaskType: create.asyncThunk(
      async (payload: { taskTypeId: TaskTypeId; isCurrentlyArchived: boolean }, { dispatch }) => {
        await dispatch(
          updateTaskType({
            id: payload.taskTypeId,
            status: payload.isCurrentlyArchived ? TASK_TYPE_STATUS.ACTIVE : TASK_TYPE_STATUS.ARCHIVED,
          }),
        );
      },
      {},
    ),
    deleteTaskType: create.asyncThunk(
      async (payload: { taskTypeId: TaskTypeId; clusterId: ClusterId }, { dispatch }) => {
        await alerting(() => deleteApiTaskType(payload.taskTypeId), { dispatch });
        success('TaskType.snack_delete', { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          if (arg.clusterId) {
            state.taskTypesByClusterId[arg.clusterId] ??= [];
            state.taskTypesByClusterId[arg.clusterId] = state.taskTypesByClusterId[arg.clusterId]!.filter(
              (taskType) => taskType.id !== arg.taskTypeId,
            );
          }
        },
      },
    ),
  }),
  selectors: {
    selectTaskTypesByClusterId: (state) => state.taskTypesByClusterId,
    selectTaskTypeTemplates: (state) => state.taskTypeTemplates,
    selectPhytoTaskTypes: (state) => state.phytoTaskTypes,
  },
});

export const { fetchAllTaskTypes, createTaskType, updateTaskType, archiveTaskType, deleteTaskType } =
  taskTypesSlice.actions;
export const { selectTaskTypesByClusterId, selectTaskTypeTemplates, selectPhytoTaskTypes } = taskTypesSlice.selectors;

export default taskTypesSlice.reducer;
